import { CButton, CCard, CCardBody, CDropdownItem } from '@coreui/react-pro'
import React, { useEffect, useState } from 'react'
import AdditionalInfo from './AdditionalInfo'
import BillingSummaryCodeSection from './BillingSummaryCodeSection'

import { useSelector, useDispatch } from 'react-redux'
import { listPlaceOfService, saveBillingNotes } from 'src/helper/backendHelper'
import { toast } from 'react-toastify'
import { getAiPredictions } from '../../../redux/progressNote/progressNoteThunk'
import ProgressNoteActionButtons from './ProgressNoteActionButtons'
import MIPSCard from '../../../components/NewEncounter/MIPSCard'
import { changeMIPSdata } from '../../../redux/progressNote/progressNoteSlice'
import { createTemplateWithValue } from '../../../helper/formater'
import FEATURES from '../../../utils/featuresConstants'
import DateOfServiceCard from '../../../components/NewEncounter/DateOfServiceCard/DateOfServiceCard'
import FacilityInfoCard from '../../../components/NewEncounter/FacilityCard/FacilityInfoCard'
import { convertSlashDateToHyphenDate } from '../../../utils/sharedFunctions'
import LTCToggleBox from '../../../components/common/ToggleBox/LTCToggleBox'
import { getIsAi, showLongTermCare } from '../../../utils/sharedFeaturesFunctions'
import AdditionalAttachments from '../../../components/NewEncounter/AdditionalAttachmentEncounter/AdditionalAttachments'
import { billVisitErrorCheck } from '../../../components/common/shared/sharedEncounterLogic'

// because MIPS are used differently in encounters versus notes,
// we need to ensure that passing MIPS in has the option object as the value
// while the on save below saves the option label string as the value
function getMipsFromNote(noteData) {
  if (noteData?.data?.find((item) => item?.title === 'MIPS')) {
    return (
      noteData?.data
        ?.find((item) => item?.title === 'MIPS')
        ?.sections?.map((mip) => {
          return { ...mip, value: mip?.options.find((opt) => opt?.label === mip?.value) }
        }) || []
    )
  }
  return []
}

const BillingSummary = ({
  allFacilites,
  onBillingSave,
  onSignAndBill,
  visit,
  encounterType,
  billingTxt,
  setBillingTxt,
  selectedSharedProvider,
  setSelectedSharedProvider,
  incidentToProviderSubstantive,
  setIncidentToProviderSubstantive,
  isTeleHealth,
  setIsTeleHealth,
  isHospiceRelated,
  setIsHospiceRelated,
  isCareCoordination,
  setIsCareCoordination,
  isLongTermCare,
  setIsLongTermCare,
  isNeuroSuite,
  setIsNeuroSuite,
  isPendingCosign,
  setIsPendingCosign,
  setBillingEntity,
  billingEntity,
  billingEntityOptions,
  selectedPlaceOfService,
  setSelectedPlaceOfService,
  careTeam,
  setCareTeam,
  isValidToSign,
  dateOfService,
  setDateOfService,
  selectedFacilityID,
  setSelectedFacilityID,
  setSelectedFacilityName,
}) => {
  const dispatch = useDispatch()
  const [procedureCodesAreValidForEncounterType, setProcedureCodesAreValidForEncounterType] =
    useState(false)
  const visitDetails = useSelector((state) => state?.progressNote?.visitDetails)
  const procedureCodesSelected = useSelector((state) => state?.progressNote?.procedureCodesSelected)
  const diagnosisCodesSelected = useSelector((state) => state?.progressNote?.diagnosisCodesSelected)

  // importing both to match what ProgressNote does with MIPS
  const selectedTemplate = useSelector((state) => state.progressNote?.selectedTemplate)
  const selectedSections = useSelector((state) => state.progressNote?.selectedSections)

  const features = useSelector((state) => state?.user?.features)
  const provider = useSelector((state) => state?.user?.provider)
  const practice = useSelector((state) => state?.user?.practice)
  const roles = useSelector((state) => state?.user?.roles) || []

  const [thisFacility, setThisFacility] = useState()

  const [listPOS, setListPOS] = useState([])

  const canUseAi = getIsAi() && features?.includes(FEATURES.DesktopAI)

  useEffect(() => {
    setThisFacility(allFacilites?.find((item) => item?.facility_id === visitDetails?.facility_id))
  }, [visitDetails?.facility_id, allFacilites])

  useEffect(() => {
    if (canUseAi) {
      dispatch(getAiPredictions())
    }
  }, [dispatch, canUseAi])

  useEffect(() => {
    listPlaceOfService().then((res) => {
      if (res?.status === 'success') {
        let tempPOS = res?.results?.filter((item) =>
          thisFacility?.places_of_service?.includes(item?.code),
        )

        setListPOS(tempPOS)

        if (
          tempPOS?.length > 0 &&
          !tempPOS.find((tempPOS) => tempPOS.code === selectedPlaceOfService)
        ) {
          setSelectedPlaceOfService(tempPOS[0]?.code)
        }
      }
    })
  }, [thisFacility, selectedPlaceOfService, setSelectedPlaceOfService])

  const onBillingSign = () => {
    toast.dismiss()

    const billHasErrors = billVisitErrorCheck({
      diagnosisCodes: diagnosisCodesSelected,
      encounterType: visit?.encounterType,
      procedureCodes: procedureCodesSelected,
      provider,
      roles,
      service_date_from: visit?.service_date_from,
    })

    if (billHasErrors) {
      // toasts go off in error check
      return
    }

    saveBillingNotes(visit?._id, { billing_notes: billingTxt })

    if (onSignAndBill) {
      onSignAndBill()
    }
  }

  const onBillingPendingReview = () => {
    onBillingSave(true)
  }

  const processStop = (procedureCodesValidForEncounterType) => {
    setProcedureCodesAreValidForEncounterType(procedureCodesValidForEncounterType)
  }

  return (
    <CCard>
      <CCardBody className={'billsummary-card-body'}>
        <div className="billing-summary-holder">
          <div className="billing-summary">
            <div className="section-holder">
              <DateOfServiceCard
                setDateOfService={(newDOS) => {
                  setDateOfService(convertSlashDateToHyphenDate(newDOS))
                }}
                dateOfService={dateOfService}
              />
              <FacilityInfoCard
                setSelectedFacility={(newFacility) => {
                  setSelectedFacilityName(newFacility)
                }}
                selectedFacilityId={selectedFacilityID}
                setSelectedFacilityId={(newFacility) => {
                  setSelectedFacilityID(newFacility)
                }}
                showPOS={features?.includes(FEATURES.PlaceOfServiceOverride)}
                pos={selectedPlaceOfService}
                onNewPOSSelected={(newPOS) => {
                  setSelectedPlaceOfService(newPOS)
                  setIsLongTermCare(false)
                }}
                telehealth={isTeleHealth}
              ></FacilityInfoCard>
              {showLongTermCare(features, selectedPlaceOfService) && (
                <CCard>
                  <LTCToggleBox
                    onToggleChange={(value) => {
                      setIsLongTermCare(value)
                    }}
                    isToggledOn={isLongTermCare}
                    padding={true}
                  ></LTCToggleBox>
                </CCard>
              )}
            </div>
            {/* cpt */}
            <div className="section-holder">
              <BillingSummaryCodeSection
                section={{ type: 'procedures' }}
                title={'Procedure Codes'}
                pos={listPOS?.length > 0 ? selectedPlaceOfService : visitDetails?.place_of_service}
                encounterType={encounterType}
                dateOfService={visitDetails?.service_date_from}
                processStop={processStop}
              />
            </div>
            {/* DX */}
            <div className="section-holder">
              <BillingSummaryCodeSection
                section={{ type: 'diagnosis' }}
                title={'Diagnosis Codes'}
                patientId={visitDetails?.patient_id}
                visitId={visitDetails?._id}
                isPcc={visitDetails?.points_click_care_patient}
                dateOfService={visitDetails?.service_date_from}
              />
            </div>

            {/*MIPS*/}
            {features?.includes(FEATURES.VisitNotesMIPS) && (
              <MIPSCard
                mipsValueData={getMipsFromNote(
                  createTemplateWithValue(selectedTemplate, selectedSections),
                )}
                setMipsValueData={(newMips) => {
                  dispatch(
                    changeMIPSdata(
                      newMips.map((mip) => {
                        // Notes require the MIPS value to be the label string
                        return { ...mip, value: mip?.value?.label }
                      }),
                    ),
                  )
                }}
              />
            )}
            {canUseAi && (
              <>
                <AdditionalAttachments
                  patientId={visitDetails?.patient_id}
                  chargeId={visitDetails?._id}
                  title={'Add Attachments'}
                />
                <AdditionalInfo
                  allFacilites={allFacilites}
                  visit={visit}
                  setBillingTxt={setBillingTxt}
                  billingTxt={billingTxt}
                  selectedSharedProvider={selectedSharedProvider}
                  setSelectedSharedProvider={setSelectedSharedProvider}
                  incidentToProviderSubstantive={incidentToProviderSubstantive}
                  setIncidentToProviderSubstantive={setIncidentToProviderSubstantive}
                  isTeleHealth={isTeleHealth}
                  setIsTeleHealth={setIsTeleHealth}
                  isHospiceRelated={isHospiceRelated}
                  setIsHospiceRelated={setIsHospiceRelated}
                  isCareCoordination={isCareCoordination}
                  setIsCareCoordination={setIsCareCoordination}
                  setBillingEntity={setBillingEntity}
                  billingEntity={billingEntity}
                  billingEntityOptions={billingEntityOptions}
                  careTeam={careTeam}
                  setCareTeam={setCareTeam}
                  isNeuroSuite={isNeuroSuite}
                  setIsNeuroSuite={setIsNeuroSuite}
                  isPendingCosign={isPendingCosign}
                  setIsPendingCosign={setIsPendingCosign}
                />
              </>
            )}
          </div>
          {canUseAi && (
            <div className="billing-summary-footer">
              <ProgressNoteActionButtons
                SignButton={() => (
                  <CButton
                    className="progress-note-billing-summary-dropdown-main"
                    color="primary"
                    onClick={onBillingSign}
                    disabled={
                      practice?.medical_coding &&
                      provider?.medical_coding !== false &&
                      !roles.includes('Claimocity App Biller')
                        ? false
                        : !(isValidToSign && procedureCodesAreValidForEncounterType)
                    }
                  >
                    {provider?.medical_coding ? 'Sign Note' : 'Sign and bill'}
                  </CButton>
                )}
                SaveButton={() => (
                  <CDropdownItem
                    className="progress-note-billing-summary-item progress-note-billing-summary-item-draft"
                    onClick={() => onBillingSave()}
                  >
                    <div className="progress-note-billing-summary-item-text">Save as draft</div>
                  </CDropdownItem>
                )}
                PendingReviewButton={() => (
                  <CDropdownItem className="progress-note-billing-summary-item progress-note-billing-summary-item-pending-review">
                    <div
                      className="progress-note-billing-summary-item-text"
                      onClick={onBillingPendingReview}
                    >
                      Pending review
                    </div>
                  </CDropdownItem>
                )}
              />
            </div>
          )}
        </div>
      </CCardBody>
    </CCard>
  )
}

export default BillingSummary
